import type {
  AuthConfig,
  SetFormType,
  SetSharedState,
} from 'src/components/AuthNew/types';
import {
  AuthMethodsForAnalytics,
  AuthFormType,
  RequestStatus,
} from 'src/components/AuthNew/types';

import { api } from 'src/api';
import type { FullClientData } from '@lt/api/dist/types/client';
import type { AxiosError } from 'axios';
import { flowAsync } from 'src/utils/fp';
import { cond, flow, get, isNil, negate } from 'lodash/fp';
import type {
  PostClientAuth,
  PostClientAuthResponse,
  PostClientSocialNetworkAuth,
} from '@lt/api/dist/methods/client/types';
import type { CondPairUnary } from 'lodash';
import {
  isPhoneExist,
  isPhoneNumber,
  mergeFullClientDataWithSharedState,
} from 'src/components/AuthNew/utils';
import { pushAuthSuccessful } from '../analytics';

type Props = {
  setAuthRequestStatus: (type: RequestStatus) => void;
  onSuccess: (client: FullClientData) => void;
  onError: (error: AxiosError) => void;
  setSharedState: SetSharedState;
  setFormType: SetFormType;
};

const isPhoneNotConfirm = negate(isPhoneExist);

const ifPhoneNotConfirm =
  (setSharedState: SetSharedState) =>
  (
    setFormType: Props['setFormType'],
    onSuccess: Props['onSuccess'],
  ): CondPairUnary<PostClientAuthResponse, PostClientAuthResponse> =>
    [
      isPhoneNotConfirm,
      (response) => {
        setSharedState(mergeFullClientDataWithSharedState(response.client));
        onSuccess(response.client);
        setFormType(AuthFormType.SetPhoneForConfirm);
        return response;
      },
    ];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPhoneConfirm = flow<any, string | null, boolean>(
  get(['client', 'unconfirmed_phone']),
  isNil,
);

const ifPhoneConfirm = (
  onSuccess: Props['onSuccess'],
): CondPairUnary<PostClientAuthResponse, PostClientAuthResponse> => [
  isPhoneConfirm,
  (response: PostClientAuthResponse) => {
    onSuccess(response.client);
    return response;
  },
];

const postAuth = (
  data: PostClientAuth | PostClientSocialNetworkAuth,
): Promise<PostClientAuthResponse> =>
  api.postClientAuth({ ...data, use_cookies: true });

const ifAuthNotSuccess = (): CondPairUnary<
  PostClientAuthResponse,
  PostClientAuthResponse
> => [
  (response) => !response.success,
  () => {
    throw Error('Auth not success');
  },
];

export const useAuth = ({
  setAuthRequestStatus,
  onSuccess,
  setSharedState,
  setFormType,
  onError,
}: Props) => {
  const handleAuth = async ({ login, password }: AuthConfig): Promise<void> => {
    setAuthRequestStatus(RequestStatus.Submitting);
    try {
      await flowAsync(
        postAuth,
        cond<PostClientAuthResponse, PostClientAuthResponse>([
          ifAuthNotSuccess(),
          ifPhoneNotConfirm(setSharedState)(setFormType, onSuccess),
          ifPhoneConfirm(onSuccess),
        ]),
      )({ login, password });
      pushAuthSuccessful(
        isPhoneNumber(login)
          ? AuthMethodsForAnalytics.Phone
          : AuthMethodsForAnalytics.Email,
      );

      setAuthRequestStatus(RequestStatus.Succeeded);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setAuthRequestStatus(RequestStatus.Failed);
      onError(error as AxiosError);
    }
  };

  return { handleAuth };
};
